import { useEffect, useState } from "react";
import { SOURCE_TYPE_LABELS, CASE_STATUS_LABELS } from "../types";
import type { CaseStatus, Direction, SourceType } from "../types";
import { formatDate } from "../lib/formatDate";

interface PublicRecord {
  id: string;
  record_date: string;
  logged_at: string;
  direction: Direction;
  source_type: SourceType;
  from_addr: string | null;
  to_addr: string | null;
  subject: string | null;
  body_text: string | null;
  file_name: string | null;
  file_mime_type: string | null;
  forwarded_via: string | null;
  pinned: number;
}

interface PublicContact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
}

interface PublicCase {
  id: string;
  name: string;
  description: string | null;
  status: CaseStatus;
  deadline: string | null;
  summary: string | null;
  created_at: string;
}

interface Payload {
  case: PublicCase;
  records: PublicRecord[];
  contacts: PublicContact[];
  expires_at: string;
}

const API_BASE =
  import.meta.env.VITE_API_BASE ?? "https://api.casethread.app";

export default function PublicShareView({ token }: { token: string }) {
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // credentials: "omit" so a logged-in viewer visiting a share
        // link doesn't accidentally send their Clerk session cookies
        // to the public endpoint. The share token in the URL is the
        // only authorization that should apply here.
        const res = await fetch(`${API_BASE}/share/${token}`, {
          credentials: "omit",
        });
        if (res.status === 410) {
          const body = await res.json().catch(() => ({}));
          setError(
            body.error === "revoked"
              ? "This link has been revoked by the case owner."
              : "This link has expired.",
          );
          return;
        }
        if (!res.ok) {
          setError(
            res.status === 404
              ? "This link is invalid or no longer exists."
              : `Unable to load share (HTTP ${res.status}).`,
          );
          return;
        }
        setData((await res.json()) as Payload);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <div className="loading">Loading shared case…</div>;
  if (error) {
    return (
      <div className="public-share">
        <div className="public-share-empty">
          <h1>Unavailable</h1>
          <p>{error}</p>
          <p>
            <a href="/welcome">Go to CaseThread</a>
          </p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const sortedRecords = [...data.records].sort((a, b) =>
    a.record_date > b.record_date
      ? -1
      : a.record_date < b.record_date
        ? 1
        : 0,
  );
  const pinned = sortedRecords.filter((r) => !!r.pinned);
  const rest = sortedRecords.filter((r) => !r.pinned);

  return (
    <div className="public-share">
      <header className="public-share-header">
        <div className="public-share-brand">
          CaseThread · <span className="muted">shared case (read-only)</span>
        </div>
        <div className="public-share-expiry muted">
          Link expires {formatDate(data.expires_at, "short")}
        </div>
      </header>

      <main className="public-share-main">
        <h1 className="public-share-title">{data.case.name}</h1>
        <div className={`status-select status-${data.case.status} public-status`}>
          <span>{CASE_STATUS_LABELS[data.case.status]}</span>
        </div>

        {data.case.description && (
          <p className="public-share-desc">{data.case.description}</p>
        )}

        {data.case.deadline && (
          <p className="public-share-deadline">
            Deadline: <strong>{formatLong(data.case.deadline)}</strong>
          </p>
        )}

        {data.case.summary && (
          <section className="public-share-section">
            <h2>Summary</h2>
            <p>{data.case.summary}</p>
          </section>
        )}

        {data.contacts.length > 0 && (
          <section className="public-share-section">
            <h2>Contacts</h2>
            <ul className="public-share-contacts">
              {data.contacts.map((c) => (
                <li key={c.id}>
                  <strong>{c.name}</strong>
                  {c.role && <> — {c.role}</>}
                  {c.email && (
                    <div>
                      <a href={`mailto:${c.email}`}>{c.email}</a>
                    </div>
                  )}
                  {c.phone && <div>{c.phone}</div>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {pinned.length > 0 && (
          <section className="public-share-section">
            <h2>📌 Pinned records</h2>
            {pinned.map((r) => (
              <RecordBlock key={r.id} r={r} />
            ))}
          </section>
        )}

        <section className="public-share-section">
          <h2>Records ({sortedRecords.length})</h2>
          {rest.length === 0 && pinned.length === 0 && (
            <p className="muted">No records on this case yet.</p>
          )}
          {rest.map((r) => (
            <RecordBlock key={r.id} r={r} />
          ))}
        </section>
      </main>

      <footer className="public-share-footer">
        <span>Generated by CaseThread</span>
        <span>
          <a href="/privacy">Privacy</a> · <a href="/terms">Terms</a>
        </span>
      </footer>
    </div>
  );
}

function RecordBlock({ r }: { r: PublicRecord }) {
  return (
    <article className="public-record">
      <header className="public-record-head">
        <span className="public-record-meta">
          {r.direction === "inbound" ? "↓ In" : "↑ Out"} ·{" "}
          {SOURCE_TYPE_LABELS[r.source_type]} · {formatLong(r.record_date)}
        </span>
        {r.subject && <h3 className="public-record-subject">{r.subject}</h3>}
      </header>
      <dl className="public-record-dl">
        {r.from_addr && (
          <>
            <dt>From</dt>
            <dd>{r.from_addr}</dd>
          </>
        )}
        {r.to_addr && (
          <>
            <dt>To</dt>
            <dd>{r.to_addr}</dd>
          </>
        )}
        {r.file_name && (
          <>
            <dt>Attached</dt>
            <dd>
              {r.file_name} ({r.file_mime_type ?? "file"})
            </dd>
          </>
        )}
      </dl>
      {r.body_text && <pre className="public-record-body">{r.body_text}</pre>}
    </article>
  );
}

function formatLong(iso: string): string {
  return formatDate(iso, "long") || iso;
}
